import { memo, useEffect, useState } from "react"
import { Disclaimer } from "@/components/resources/components/Disclaimer"
import { ArticleHero } from "@/components/resources/components/Hero"
import { NewsletterTeaser } from "@/components/resources/components/TeaserGrid/NewsletterTeaser"
import type { BeehiivPostWithContent } from "@/components/resources/data/beehiiv/client"

const applyStyles = (
  element: HTMLElement,
  styles: Partial<CSSStyleDeclaration>
) => {
  Object.assign(element.style, styles)
}

const addClassNames = (element: HTMLElement, className: string) => {
  element.classList.add(...className.split(" "))
}

// Hacky way to fix beehiiv's incoming styles
const elementStyleOverrides = {
  divider: {
    match: (element: HTMLElement) => {
      return element.style.borderTop === "1px solid rgb(192, 192, 192)"
    },
    applyStyles: (element: HTMLElement) => {
      applyStyles(element, { width: "100%", borderTop: "" })
      addClassNames(element, "border-t border-solid border-border-default")
    },
  },
  card: {
    match: (element: HTMLElement) => {
      return (
        element.style.borderColor === "rgb(192, 192, 192)" &&
        element.style.borderRadius === "15px"
      )
    },
    applyStyles: (element: HTMLElement) => {
      applyStyles(element, {
        borderColor: "",
        borderRadius: "",
        backgroundColor: "",
        padding: "8px 12px",
      })
      addClassNames(
        element,
        "bg-white dark:bg-neutral-900 rounded-[32px] border border-solid border-border-default"
      )
    },
  },
  nestedCard: {
    match: (element: HTMLElement) =>
      element.style.backgroundColor === "rgb(246, 246, 246)" &&
      element.style.borderRadius === "5px",
    applyStyles: (element: HTMLElement) => {
      element.removeAttribute("style")
      applyStyles(element, {
        padding: "8px 20px 20px",
      })
      addClassNames(
        element,
        "bg-neutral-100 dark:bg-neutral-900 rounded-lg border border-solid border-border-default"
      )
    },
  },
  blockQuote: {
    match: (element: HTMLElement) => {
      return (
        element.style.backgroundColor === "rgb(255, 255, 255)" &&
        element.style.borderLeftColor === "rgb(1, 69, 54)"
      )
    },
    applyStyles: (element: HTMLElement) => {
      element.style.backgroundColor = ""
    },
  },
}

const parseBeehiivHTMLContent = (htmlContent: string) => {
  const html = new DOMParser().parseFromString(htmlContent ?? "", "text/html")
  const contentElement = html.body.querySelector("#content-blocks")

  let unstyledBody = null
  if (contentElement) {
    const clonedContent = contentElement.cloneNode(true) as HTMLElement
    // clonedContent.querySelectorAll("style").forEach((style: HTMLElement) => {
    //   style.remove()
    // })
    clonedContent
      .querySelectorAll("[style]")
      // @ts-expect-error
      .forEach((element: HTMLElement) => {
        // Remove color, font family, font weight, line height
        element.style.color = ""
        element.style.fontFamily = ""
        element.style.fontWeight = ""
        element.style.lineHeight = ""
        element.style.textDecoration = ""

        Object.values(elementStyleOverrides)
          .find((override) => override.match(element))
          ?.applyStyles(element)
      })
    unstyledBody = clonedContent.innerHTML
  }

  return { body: contentElement?.innerHTML ?? "", unstyledBody }
}

const ArticleContent = memo(
  ({ htmlContent }: { htmlContent: string }) => {
    const { unstyledBody } = parseBeehiivHTMLContent(htmlContent ?? "")
    return (
      <>
        <div className="max-w-3xl mx-auto px-4">
          {/* Unstyled */}
          {/* <div
            id="beehiiv"
            dangerouslySetInnerHTML={{ __html: htmlContent ?? "" }}
          /> */}
          {/* Styled */}
          <div
            id="beehiiv-unstyled"
            dangerouslySetInnerHTML={{ __html: unstyledBody ?? "" }}
            className="beehiiv-content"
          />
          <Disclaimer />
        </div>
        <div className="flex justify-center mb-8 md:mb-12 lg:mb-16 pt-8 md:pt-12 lg:pt-16 max-w-5xl mx-auto px-4">
          <NewsletterTeaser as="section" />
        </div>
      </>
    )
  },
  () => true
) // No need to re-render this even if possible parent layout re-renders

ArticleContent.displayName = "ArticleContentRenderer"

export const BeehiivArticlePage = ({
  post,
}: {
  post: BeehiivPostWithContent
}) => {
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  if (!isMounted) {
    return <div>Loading...</div>
  }

  return (
    <article className="mb-8 md:mb-12 lg:mb-16">
      <div className="max-w-7xl mx-auto px-4">
        <ArticleHero
          categoryName="Newsletter"
          category="newsletter"
          title={post.title}
          subtitle={post.subtitle}
          image={post.thumbnail_url}
          publishedAt={
            post.publish_date
              ? new Date(post.publish_date * 1000).toISOString()
              : ""
          }
          releaseNumber={1}
          projectMeta={null}
          author={{
            // The byline links to this author page
            id: "token-terminal",
            slug: "token-terminal",
            nickname: "Newsletter",
            firstname: "Token",
            lastname: "Terminal",
            title: "",
            image: "",
            isGuest: false,
            schemaType: "Organization",
            socials: [],
          }}
        />
      </div>
      <ArticleContent htmlContent={post.content?.premium.web ?? ""} />
      {/* {related?.length ? (
        <section className="pt-8 md:pt-12 lg:pt-16 mb-8 md:mb-12 lg:mb-16 max-w-7xl mx-auto px-4">
          <div className="flex flex-col gap-6">
            <h2 className="text-xl font-medium">Continue reading</h2>
            <TeaserGrid teasers={related} />
          </div>
        </section>
      ) : null} */}
      {/* TODO: Bring this back */}
      {/* {false && <ArticleSchema article={article} />} */}
    </article>
  )
}
